import React, {Dispatch, FC, SetStateAction, useMemo} from "react";
import {CustomDrawer} from "../../../UI/AntCustom/CustomDrawer";
import {PlanePhotos} from "../../../../pages/Lk/OperatorsOnly/Fleet/components/PlanePhotos";
import {FlightDrawerPlane} from "../plane/FlightDrawerPlane";
import {FlightDrawerOperator} from "../operator/FlightDrawerOperator";
import {CustomButton} from "../../../UI/AntCustom/CustomButton";
import ChatIcon from "../../../../media/icons/chat_white.svg?react";
import {FlightDrawerContentWrapper, FlightDrawerPhotosAndContentWrapper} from "./FlightDrawerStyledComponents";
import {FlightDrawerOrderRequestOrderDetails} from "../details/FlightDrawerOrderRequestOrderDetails";
import {defineMessages, useIntl} from "react-intl";
import {
  IFreighterOrderRequest
} from "../../../../store/reducers/freighterRequestsReducer/freighterRequestsReducerTypes";
import {FlightDrawerPrice} from "../price/FlightDrawerPrice";
import {OrderRequestPlaneStatusEnum} from "../../../../utils/constans/statuses/orderRequestStatuses";
import {useCurrencyConverter} from "../../../../hooks/useCurrencyConverter";
import {getBaseCurrencyId} from "../../../../utils/helpers/currencyConverter/currencyConverter";


const OrderRequestOrderRouteFlightDrawerMessages = defineMessages({
  writeToChat: {
    id: "orderRequestOrderRouteFlightDrawer.writeToChat",
    defaultMessage: "Send message",
  },
})

interface IOrderRequestOrderRouteFlightDrawer {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  orderRequest: IFreighterOrderRequest;
  openChat: () => void;
}

export const OrderRequestOrderRouteFlightDrawer: FC<IOrderRequestOrderRouteFlightDrawer> = ({
                                                                                              isOpen,
                                                                                              setIsOpen,
                                                                                              orderRequest,
                                                                                              openChat,
                                                                                            }) => {
  const intl = useIntl();
  const {convertSync, isReady} = useCurrencyConverter();
  const baseCurrencyId = getBaseCurrencyId();

  // Find approved plane from planeList (status 5 = STATUS_APPROVED_BY_BROKER)
  const approvedPlane = useMemo(() => {
    if (!orderRequest.planeList || orderRequest.planeList.length === 0) {
      return null;
    }
    return orderRequest.planeList.find(plane => plane.orderRequestStatusId === OrderRequestPlaneStatusEnum.STATUS_APPROVED_BY_BROKER) || null;
  }, [orderRequest.planeList]);

  // Get the plane object (either from approved plane or old structure)
  const plane = approvedPlane?.plane || orderRequest.plane;

  // Calculate price in aircraft's currency
  const {displayPrice, displayCurrency} = useMemo(() => {
    // If we have an approved plane, use its price and convert to aircraft currency
    if (approvedPlane) {
      const operatorPrice = approvedPlane.operatorPrice ? parseFloat(approvedPlane.operatorPrice) : null;
      const operatorCurrency = approvedPlane.operatorCurrency || approvedPlane.currency;
      const aircraftCurrency = approvedPlane.plane?.flyHourCurrency;

      // If we have operator price and aircraft currency, convert if needed
      if (operatorPrice !== null && operatorCurrency && aircraftCurrency && isReady && baseCurrencyId) {
        // If currencies match, no conversion needed
        if (operatorCurrency.id === aircraftCurrency.id) {
          return {
            displayPrice: operatorPrice,
            displayCurrency: aircraftCurrency
          };
        }

        // Convert from operator currency to aircraft currency
        try {
          const convertedPrice = convertSync(operatorPrice, operatorCurrency.id, aircraftCurrency.id);
          return {
            displayPrice: Math.round(convertedPrice * 100) / 100,
            displayCurrency: aircraftCurrency
          };
        } catch (error) {
          console.error('[OrderRequestOrderRouteFlightDrawer] Failed to convert price to aircraft currency:', error);
          // Fallback to operator price/currency
          return {
            displayPrice: operatorPrice,
            displayCurrency: operatorCurrency
          };
        }
      }

      // Fallback: use operator price/currency if conversion not possible
      if (operatorPrice !== null && operatorCurrency) {
        return {
          displayPrice: operatorPrice,
          displayCurrency: operatorCurrency
        };
      }
    }

    // Fallback to old structure
    if (orderRequest.price && orderRequest.currency) {
      return {
        displayPrice: orderRequest.price,
        displayCurrency: orderRequest.currency
      };
    }

    return {
      displayPrice: null,
      displayCurrency: null
    };
  }, [approvedPlane, orderRequest.price, orderRequest.currency, convertSync, isReady, baseCurrencyId]);

  // Show drawer if we have a plane (either approved or old structure)
  if (!plane)
    return null;

  return (
    <CustomDrawer open={isOpen} onClose={() => setIsOpen(false)}>
      <FlightDrawerPhotosAndContentWrapper>
        {plane.imageFiles && <PlanePhotos imageFiles={plane.imageFiles}/>}

        <FlightDrawerContentWrapper>
          <FlightDrawerPlane plane={plane}/>
          <FlightDrawerOrderRequestOrderDetails orderRequest={orderRequest} approvedPlane={approvedPlane}/>
          {displayPrice !== null && displayCurrency && (
            <FlightDrawerPrice price={displayPrice} currency={displayCurrency} />
          )}
          <FlightDrawerOperator managerUser={orderRequest.user}
                                company={orderRequest.company}/>
          <CustomButton type={"primary"} onClick={openChat} style={{borderRadius: 10}}>
            <ChatIcon/>
            <div>{intl.formatMessage(OrderRequestOrderRouteFlightDrawerMessages.writeToChat)}</div>
          </CustomButton>
        </FlightDrawerContentWrapper>
      </FlightDrawerPhotosAndContentWrapper>
    </CustomDrawer>
  );
};